import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppData } from "../../hooks/useAppData";
import { useAuth } from "../../auth";
import type { Booking, BookingsMap, SavePayload, SlotData } from "../../hooks/bookingTypes";
import { fmtDateApi } from "../../utils/date";
import { expectTea } from "@ub/shared";
import { getBranchConfig, TEA_MIN_DEFAULT } from "../../hooks/branchConfig";
import { hasOpenSheet, type Channel } from "../shared";
import { ModalHeader } from "./ModalHeader";
import { BookingForm } from "./BookingForm";
import { BlockForm } from "./BlockForm";
import { ModalActions } from "./ModalActions";
import { CustomTimeModal } from "./CustomTimeModal";
import { BlockReasonPrompt } from "./BlockReasonPrompt";
import { CancelReasonPrompt } from "./CancelReasonPrompt";
import { ClientInfoCard } from "./ClientInfoCard";
import { ConflictOverride } from "./ConflictOverride";
import { statusAccent } from "./statusAccent";
import { useConflictGate } from "../../hooks/useConflictGate";
import { useClientByPhone } from "../../hooks/useClientByPhone"; import { api } from "../../api/client"; import { useOverrideDayBookings } from "../../hooks/useOverrideDayBookings";
import { buildSavePayload } from "./savePayload"; import { useLearningPlayers } from "./useLearningPlayers";
import css from "./BookingModal.module.css";

/**
 * B.3 — DC Booking Modal (create state, Stage 2.2).
 *
 * Фулскрин-шит 100dvh поверх блюр-фона грида (БМ4).
 * Type toggle: OFF=lead, ON=booking (label показывает текущий тип).
 * 7 флагов: Здтк (prepaid+amount), Класс, ДР, Учеб, КНКР, СпцВ, !!!
 * + ЧАЙНАЯ toggle с 3 «доп» dropdown'ами для master_classes
 * + A15 channel-picker (TG / VK / MAX)
 * + «НЕ СТАНДАРТНЫЙ СЛОТ» → sub-modal для custom_time.
 */
export interface BookingModalProps {
  questId: string;
  /** Базовое время слота из грида. */
  time: string;
  /** Видимая дата в гриде (UTC-midnight business date). */
  curDate: Date;
  /** Уже забронированный слот (для edit-state). Stage 2.2 — null only. */
  booking: SlotData | null;
  /** Конфликтующий лид (для отображения warning). */
  conflictLead: Booking | null;
  /** Все бронирования дня — для slot-picker в header. */
  dayBookings: BookingsMap;
  /** Базовые слоты квеста (отсортированные times). */
  questSlots: string[];
  onSave: (payload: Record<string, unknown>, curDateStr: string) => Promise<void>;
  onClose: () => void; onToast: (msg: string) => void;
  /** Перезагрузка сетки после переноса (move не идёт через onSave). */ onReload: () => void;
  /** A48-коррекция: «Перенести» = move-режим ОСНОВНОЙ сетки (модалка закрывается). */
  onStartMove?: (b: Booking) => void;
  initialShowInfo?: boolean; // диплинк «Информация клиента» (24.08): открыть карточку сразу
  /** 17.09: слот сдвинут мастер-классом — новая бронь открывается сразу на 17:45 (time = custom_time). */
  initialCustomTime?: string;
  /** 17.09: игра стоит на слоте, который МК (04.10: или чайная) сдвинул, — текст плашки (title на таче не виден); у блока «Чайная» — что он лишний. */
  clash?: string;
}

export function BookingModal(props: BookingModalProps) {
  const {
    questId, time, curDate, booking: rawBooking, conflictLead,
    dayBookings, questSlots,
    onSave, onClose, onToast, onReload, onStartMove, initialShowInfo, initialCustomTime, clash,
  } = props;
  const { branches, quests, leadSources, masterClasses, teaRooms } = useAppData();
  const auth = useAuth();
  const userId = auth.user?.staffId ?? "";

  /* Клавиатура ОС (A46-коррекция): видимость поля держит useKeyboardViewportFix,
     навешанный точечно на имя/комментарий внутри BookingForm — не здесь. */
  const quest = useMemo(() => quests.find((q) => q.id === questId), [quests, questId]);
  if (!quest) return null;

  const isNew = !rawBooking || rawBooking.type === "auto-block";
  const booking = rawBooking && rawBooking.type !== "auto-block" ? (rawBooking as Booking) : null;
  const isBlock = booking?.type === "block";

  const brCfg = getBranchConfig(branches, quest.branch_id);
  const branchTeaRooms = teaRooms.filter((r) => r.branch_id === quest.branch_id);
  /* 06.09 (владелец: «в Амонг Ас нельзя поставить никакой МК, кроме Лабубу»): филиал услуги — не фильтр,
     а порядок: сначала свои и общесетевые (''), потом остальные. Любой активный МК можно поставить на любой квест. */
  const branchMcs = masterClasses.filter((m) => m.active)
    .sort((a, b) => Number(!(a.branch_id === quest.branch_id || a.branch_id === "")) - Number(!(b.branch_id === quest.branch_id || b.branch_id === "")));
  const teaMinCfg = brCfg.teaMin ?? TEA_MIN_DEFAULT;
  const curDateStr = fmtDateApi(curDate);

  /* ── Form state ─────────────────────────────────────────────────────── */

  /**
   * Default type for a fresh new slot is "lead" per screen-03 эталон
   * (Frame 1 default). DC обычно сначала заводит лида (ещё не оплачено),
   * потом конвертирует в бронь когда клиент подтверждает. Существующая
   * запись сохраняет свой собственный type при открытии.
   */
  const [formType, setFormType] = useState<"booking" | "lead">(
    booking?.type === "booking" ? "booking" : "lead",
  );
  /* Лид → бронь авто-конвертируется, как только оператор вносит реальные данные
     (имя / телефон / комментарий). Если оператор сам переключил на «лид» —
     это разовый выбор, авто-конвертацию больше не запускаем (manualLead). */
  const [manualLead, setManualLead] = useState(false);
  const bookify = () => { if (!manualLead) setFormType((t) => (t === "lead" ? "booking" : t)); };
  /* Б3-фикс: в edit берём РЕАЛЬНОЕ время брони (может быть вне сетки: custom_time
     или смена сетки слотов), а не базу колонки грида после remapCustomBookings —
     иначе «19:00» открывался как «18:00» и молча переезжал при сохранении. */
  const stdSlot = !booking || questSlots.includes(booking.time);
  const [slotTime, setSlotTime] = useState(booking && stdSlot ? booking.time : time);
  const [source, setSource] = useState(booking?.source ?? leadSources[0]?.name ?? "");
  const [players, setPlayers] = useState(String(booking?.players || ""));
  const [clientName, setClientName] = useState(booking?.clientName ?? "");
  const [phone, setPhone] = useState(booking?.phone ?? "");
  const [tea, setTea] = useState(booking?.tea ?? false);
  const [teaRoomId, setTeaRoomId] = useState(booking?.teaRoomId ?? "");
  const [extras, setExtras] = useState<string[]>(() => {
    const mc = booking?.mc ?? "none";
    return mc === "none" ? [] : mc.split(",").filter(Boolean);
  });
  const [prepaid, setPrepaid] = useState(booking?.prepaid ?? false);
  const [prepaidAmt, setPrepaidAmt] = useState(booking?.prepaidAmount || 1000); /* 0 (бронь без задатка) — не «сумма», а пусто: дефолт 1000 (25.08) */
  const [isClass, setIsClass] = useState(booking?.isClass ?? false); const [isBirthday, setIsBirthday] = useState(booking?.isBirthday ?? false);
  const [isLearning, setIsLearning] = useState(booking?.isLearning ?? false);
  const lp = useLearningPlayers(booking?.id, isLearning, (n) => { if (n) { setPlayers(String(n)); bookify(); } setValidationErr(""); }); /* 13.10: «Кто играет» — выбрали сотрудников: кол-во = их число, лид → бронь */
  const [isCompetition, setIsCompetition] = useState(booking?.isCompetition ?? false);
  const [isSpecialEvent, setIsSpecialEvent] = useState(booking?.isSpecialEvent ?? false);
  const [specialVersionId, setSpecialVersionId] = useState(booking?.specialVersionId ?? "");
  const [specialVersions, setSpecialVersions] = useState<{ id: string; title: string }[]>([]);
  useEffect(() => {
    let live = true;
    api<{ items: { id: string; title: string }[] }>("/api/bookings/special-versions")
      .then((r) => { if (live) setSpecialVersions(r.items ?? []); })
      .catch(() => {});
    return () => { live = false; };
  }, []);
  /* Снятую с продажи версию сервер в список не отдаёт (active=1), а бронь на ней
     уже стоит — подмешиваем её сами, иначе пикер пуст и одно касание отвязывает версию. */
  const versionOptions = booking?.specialVersionId && booking.specialVersionTitle
    && !specialVersions.some((v) => v.id === booking.specialVersionId)
    ? [...specialVersions, { id: booking.specialVersionId, title: booking.specialVersionTitle }]
    : specialVersions;
  /* 22.09 (владелец): шапка модалки называет игру спецверсией, а не базовым квестом:
     сначала выбранная в пикере (имя меняется сразу), потом — название с сервера. */
  const gameName =
    (isSpecialEvent
      && (versionOptions.find((v) => v.id === specialVersionId)?.title || booking?.specialVersionTitle))
    || quest.name;
  const [isPriority, setIsPriority] = useState(booking?.isPriority ?? false);
  /* Default channel = "tg" (screen-03 эталон, Frame 1): большинство обращений
     сети идёт через Telegram. Существующие брони хранят свой channel. */
  const [channel, setChannel] = useState<Channel>(
    (booking?.channel ?? (isNew ? "tg" : "")) as Channel,
  );
  const [tg, setTg] = useState(booking?.tg ?? "");
  const [comment, setComment] = useState(booking?.comment ?? "");
  const [customTime, setCustomTime] = useState(booking?.customTime ?? (stdSlot ? (booking ? "" : initialCustomTime ?? "") : booking?.time ?? ""));
  /**
   * Override-date — when DC picks a different day in the modal header
   * calendar, this captures the new business-date for the booking. Empty =
   * stick with the grid's curDate.
   */
  const [overrideDateStr, setOverrideDateStr] = useState<string>("");
  const [showCustomTime, setShowCustomTime] = useState(false);
  const [showBlockReason, setShowBlockReason] = useState(false);
  const [showCancelReason, setShowCancelReason] = useState(false);
  const [showInfo, setShowInfo] = useState(!!initialShowInfo); const [saving, setSaving] = useState(false);
  const [validationErr, setValidationErr] = useState("");
  const clientHit = useClientByPhone(phone, !booking?.id); /* 15.08: live-поиск → «Инфо» в create */
  const overrideBookings = useOverrideDayBookings(overrideDateStr && overrideDateStr !== curDateStr ? overrideDateStr : ""); /* 15.08: слоты дня из календаря шапки */
  const [showAnim, setShowAnim] = useState(false);
  const gate = useConflictGate((err) => {
    setValidationErr(err instanceof Error ? err.message : "Ошибка сохранения"); setSaving(false);
  });

  /* Open animation: next frame → translate-up. */
  useEffect(() => {
    /* 02.09: rAF + таймаут-фолбэк — в фоновой/скрытой вкладке rAF не тикает, шит оставался за экраном */
    const raf = requestAnimationFrame(() => setShowAnim(true)); const to = window.setTimeout(() => setShowAnim(true), 80);
    return () => { cancelAnimationFrame(raf); window.clearTimeout(to); };
  }, []);

  const handleClose = useCallback(() => {
    setShowAnim(false);
    window.setTimeout(onClose, 220);
  }, [onClose]);

  /* Esc closes — но не пока поверх открыт пад ввода (БМ5, hasOpenSheet). */
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !hasOpenSheet()) handleClose();
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [handleClose]);

  /* Auto-tea-on for new bookings (branch config). 04.10: правило «чайная положена» — общее с сеткой и гейтом (@ub/shared). */
  useEffect(() => {
    if (!isNew || booking) return;
    setTea(expectTea({ cutoff: brCfg.teaCutoff ?? "19:00", before: !!brCfg.teaBefore, after: !!brCfg.teaAfter }, slotTime));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Derived values for header. Длительность = квест + выбранные доп.услуги (МК) + чай. */
  const extrasDuration = useMemo(
    () => extras.reduce((sum, id) => {
      const mc = masterClasses.find((m) => String(m.id) === String(id));
      return sum + (Number(mc?.duration) || 0);
    }, 0),
    [extras, masterClasses],
  );
  const totalDuration = quest.duration + extrasDuration + (tea ? teaMinCfg : 0);
  const endTime = useMemo(() => {
    const [h, m] = (customTime || slotTime).split(":").map(Number);
    const t = (h ?? 0) * 60 + (m ?? 0) + totalDuration;
    return `${String(Math.floor(t / 60) % 24).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
  }, [customTime, slotTime, totalDuration]);

  const statusLabel = isBlock ? "блок" : booking?.prepaid ? "оплачено" : isNew ? "свободно" : "бронь";

  /* ── Save handler ───────────────────────────────────────────────────── */

  const handleSave = useCallback(async () => {
    setValidationErr("");
    if (!isBlock) {
      if (!parseInt(players) || parseInt(players) < 1) { setValidationErr("Укажите кол-во игроков"); return; }
      /* 13.10 (владелец): учебная игра с выбранными сотрудниками — клиента нет, имя и телефон не требуем
         (фейковый клиент заводил бы карточку и визиты). Телефон, если начат, — всё равно целиком. */
      const staffGame = isLearning && lp.ids.length > 0, digits = phone.replace(/\D/g, "").length;
      if (staffGame && lp.over) { setValidationErr(lp.over); return; }
      if (!staffGame && !clientName.trim()) { setValidationErr(lp.loading ? "Подождите — «Кто играет» ещё загружается" : isLearning && !lp.hidden ? "Укажите имя клиента или кто играет" : "Укажите имя клиента"); return; }
      if (digits < 11 && !(staffGame && !digits)) { setValidationErr("Введите 11 цифр телефона"); return; }
    }
    setSaving(true);
    try {
      const payload = buildSavePayload({
        id: booking?.id, questId, slotTime, customTime, formType, players, clientName, phone,
        prepaid, prepaidAmt, tea, teaRoomId, teaMinCfg, extras, tg, comment, userId, source,
        isClass, isBirthday, isLearning, isCompetition, isSpecialEvent, specialVersionId,
        isPriority, channel, overrideDateStr, isBlock, learningPlayers: lp.payload,
      });
      const moved = !!booking?.id && !!overrideDateStr && overrideDateStr !== curDateStr;
      const conflicted = await gate.run(async (force?: boolean) => {
        /* 15.08: в edit смена даты календарём шапки — штатный ПЕРЕНОС (PUT /:id дату не меняет
           по канону BM4; раньше «Сохранить» с новой датой тихо её игнорировал). */
        if (moved) {
          await api(`/api/bookings/move`, { method: "PUT", body: { id: booking!.id, date: overrideDateStr, time: booking!.customTime || booking!.time, quest_id: questId, force: force || undefined, custom: booking!.customTime ? true : undefined } });
        }
        await onSave(force ? { ...payload, force: true } : payload, curDateStr);
        onToast(isNew ? (formType === "lead" ? "✦ Лид создан" : "✦ Бронь создана") : moved ? "✦ Сохранено · перенесено" : "✦ Сохранено");
        handleClose();
      }, isNew ? "ВСЁ РАВНО СОЗДАТЬ" : "ВСЁ РАВНО СОХРАНИТЬ");
      if (conflicted) setSaving(false);
    } catch (err) {
      setValidationErr(err instanceof Error ? err.message : "Ошибка сохранения");
      setSaving(false);
    }
  }, [
    isBlock, players, clientName, phone, booking, questId, customTime, slotTime, formType,
    prepaid, prepaidAmt, tea, teaRoomId, teaMinCfg, extras, tg, comment, userId, source,
    isClass, isBirthday, isLearning, isCompetition, isSpecialEvent, specialVersionId, isPriority, channel, lp.payload, lp.ids, lp.over, lp.hidden, lp.loading,
    onSave, curDateStr, overrideDateStr /* 15.08: без него стейл-замыкание слало create на исходный день */, onToast, handleClose, isNew, gate,
  ]);

  /* ── Block handler ──────────────────────────────────────────────────── */

  /**
   * Two-step block flow:
   *  1. ModalActions «ЗАБЛОКИРОВАТЬ» → opens BlockReasonPrompt sub-modal.
   *  2. BlockReasonPrompt → calls confirmBlock(reason) which submits to API.
   *
   * This replaces the previous native `window.prompt` (ugly browser dialog)
   * with the эталон-styled blue cyber-prompt.
   */
  const openBlockReason = useCallback(() => setShowBlockReason(true), []);

  const confirmBlock = useCallback(async (reason: string) => {
    setShowBlockReason(false);
    setSaving(true);
    try {
      await onSave(
        {
          questId,
          quest_id: questId,
          time: slotTime,   // 17.09: блок всегда на базовом слоте — блок на 17:45 пропадал бы из сетки
          type: "block",
          players: 0,
          client_name: "",
          phone: "",
          prepaid: false,
          prepaid_amount: 0,
          tea: false,
          mc: "none",
          telegram: "",
          comment: `БЛОК · ${reason}`,
          source: "",
          blocked_by: userId || "DC",
          block_reason: reason,
          custom_time: null,
          overrideDate: null,
        },
        curDateStr,
      );
      onToast("✦ Заблокировано");
      handleClose();
    } catch {
      setSaving(false);
    }
  }, [questId, slotTime, userId, curDateStr, onSave, onToast, handleClose]);

  /* ── Cancel handler (edit-state only) ───────────────────────────────── */

  const openCancelReason = useCallback(() => setShowCancelReason(true), []);

  /* 10.09 (владелец, со слов оператора «а как разблокировать время»): снятие блока — одним
     нажатием, без причины: блок ставил свой же сотрудник, объясняться не перед кем. */
  const unblock = useCallback(async () => {
    if (!booking?.id) return;
    setSaving(true);
    try {
      await api(`/api/bookings/${booking.id}`, {
        method: "DELETE",
        body: { cancel_reason: "Снят блок", cancelled_by: userId || "DC" },
      });
      onToast("✦ Слот разблокирован");
      handleClose();
    } catch (e: unknown) {
      setValidationErr(e instanceof Error ? e.message : "Не удалось разблокировать");
      setSaving(false);
    }
  }, [booking?.id, userId, onToast, handleClose]);

  const confirmCancel = useCallback(async (reason: string) => {
    if (!booking?.id) return;
    setShowCancelReason(false);
    setSaving(true);
    try {
      await api(`/api/bookings/${booking.id}`, {
        method: "DELETE",
        body: { cancel_reason: reason, cancelled_by: userId || "DC" },
      });
      onToast(booking.type === "lead" ? "✦ Лид удалён" : "✦ Бронь отменена");
      handleClose();
    } catch (err) {
      setValidationErr(err instanceof Error ? err.message : "Ошибка отмены");
      setSaving(false);
    }
  }, [booking, userId, onToast, handleClose]);

  /* ── Move (A48-коррекция): кнопка отдаёт бронь move-режиму ОСНОВНОЙ сетки и
     закрывает модалку; confirmMove — пик слота SlotPicker и /move custom. */
  const startMove = useCallback(() => {
    if (booking && onStartMove) { onStartMove(booking as Booking); handleClose(); }
  }, [booking, onStartMove, handleClose]);

  const confirmMove = useCallback(async (newDate: string, newTime: string, targetQuestId?: string, custom?: boolean) => {
    if (!booking?.id) return;
    setSaving(true);
    try {
      const conflicted = await gate.run(async (force?: boolean) => {
        const r = await api<{ ok: boolean; tea_reassigned?: true; tea_removed?: true }>(`/api/bookings/move`, {
          method: "PUT",
          body: { id: booking.id, date: newDate, time: newTime, quest_id: targetQuestId ?? questId, force: force || undefined, custom: custom || undefined },
        });
        onToast(r.tea_removed ? "⚠ Перенесено · чайная снята (в филиале нет чайных)" : r.tea_reassigned ? "✦ Перенесено · чайная переназначена" : "✦ Бронь перенесена");
        onReload();
        handleClose();
      }, "ВСЁ РАВНО ПЕРЕНЕСТИ");
      if (conflicted) setSaving(false);
    } catch (err) {
      setValidationErr(err instanceof Error ? err.message : "Ошибка переноса");
      setSaving(false);
    }
  }, [booking, questId, onToast, onReload, handleClose, gate]);

  /* ── Render ─────────────────────────────────────────────────────────── */

  return (
    <div
      className={`${css.overlay} ${showAnim ? css.overlayOn : ""}`}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        className={`${css.sheet} ${showAnim ? css.sheetOn : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={`${gameName} — ${statusLabel}`}
        style={
          {
            ["--sheet-accent" as string]: statusAccent(statusLabel, formType),
          } as React.CSSProperties
        }
      >
        <ModalHeader
          questName={gameName}
          bookingId={booking?.id}
          slotTime={customTime || slotTime}
          endTime={endTime}
          baseDuration={quest.duration}
          teaMin={tea ? teaMinCfg : 0}
          extrasDuration={extrasDuration}
          totalDuration={totalDuration}
          tea={tea}
          statusLabel={statusLabel}
          curDateStr={overrideDateStr || curDateStr}
          onDateChange={(iso) => setOverrideDateStr(iso === curDateStr ? "" : iso)}
          onClose={handleClose}
        />
        {clash ? (
          <div className={css.mcNote} role="status">
            {clash}
          </div>
        ) : null}

        <div className={css.body}>
          {isBlock ? (
            <BlockForm booking={booking!} comment={comment} onComment={setComment} />
          ) : (
            <BookingForm
              formType={formType}
              onTypeChange={(t) => { setFormType(t); setManualLead(t === "lead"); }}
              slotTime={slotTime}
              source={source}
              players={players}
              clientName={clientName}
              phone={phone}
              tea={tea}
              teaRoomId={teaRoomId}
              extras={extras}
              prepaid={prepaid}
              prepaidAmt={prepaidAmt}
              isClass={isClass}
              isBirthday={isBirthday}
              isLearning={isLearning} learning={lp}
              isCompetition={isCompetition}
              isSpecialEvent={isSpecialEvent}
              isPriority={isPriority}
              channel={channel}
              tg={tg}
              comment={comment}
              customTime={customTime}
              leadSources={leadSources}
              masterClasses={branchMcs}
              branchTeaRooms={branchTeaRooms}
              questId={questId}
              /* Edit: PUT /:id не меняет time — пик слота идёт штатным переносом (move: гейт, custom_time=NULL, аудит). */
              onSlotTimeChange={booking?.id
                ? (t) => void confirmMove(overrideDateStr || curDateStr, t)
                : (t) => { setSlotTime(t); setCustomTime(""); }}
              onSourceChange={setSource}
              onPlayersChange={(v) => {
                setPlayers(v);
                setValidationErr("");
              }}
              onClientNameChange={(v) => {
                setClientName(v);
                setValidationErr("");
                if (v.trim()) bookify();
              }}
              onPhoneChange={(v) => {
                setPhone(v);
                setValidationErr("");
                if (v) bookify();
              }}
              onTeaChange={setTea}
              onTeaRoomChange={setTeaRoomId}
              onExtrasChange={setExtras}
              onPrepaidChange={(v) => { setPrepaid(v); if (v && !prepaidAmt) setPrepaidAmt(1000); }}
              onPrepaidAmtChange={setPrepaidAmt}
              onClassChange={setIsClass}
              onBirthdayChange={setIsBirthday}
              onLearningChange={setIsLearning}
              onCompetitionChange={setIsCompetition}
              onSpecialEventChange={setIsSpecialEvent}
              specialVersions={versionOptions}
              specialVersionId={specialVersionId}
              onSpecialVersionChange={setSpecialVersionId}
              onPriorityChange={setIsPriority}
              onChannelChange={setChannel}
              onTgChange={setTg}
              onCommentChange={(v) => { setComment(v); if (v.trim()) bookify(); }}
              conflictLead={conflictLead}
              validationErr={validationErr}
              dayBookings={overrideBookings ?? dayBookings}
              questSlots={questSlots}
              bookingId={booking?.id}
              onInfoClick={booking?.id || clientHit.found ? () => setShowInfo(true) : undefined}
            />
          )}
          {/* 14.08: кнопки в ПОТОКЕ под контентом (не липкие) — клавиатура iOS их не возит. */}
          <ModalActions
            isNew={isNew}
            isBlock={!!isBlock}
            formType={formType}
            saving={saving}
            validationErr={validationErr}
            onSave={handleSave}
            onCustomTime={() => setShowCustomTime(true)}
            onBlock={openBlockReason}
            onMove={booking?.id && onStartMove ? startMove : undefined}
            onUnblock={isBlock && booking?.id ? unblock : undefined}
            onCancel={booking?.id ? openCancelReason : undefined}
            onClose={handleClose}
          />
        </div>

        {showCustomTime ? (
          <CustomTimeModal
            initial={customTime || slotTime}
            onApply={(t) => {
              setShowCustomTime(false);
              /* A48+: в edit время едет переносом (/move custom) — PUT /:id time не меняет. */
              if (booking?.id) { void confirmMove(overrideDateStr || curDateStr, t, undefined, true); return; }
              setCustomTime(t);
            }}
            onClear={() => {
              setCustomTime("");
              setShowCustomTime(false);
            }}
            onClose={() => setShowCustomTime(false)}
          />
        ) : null}

        {showBlockReason ? (
          <BlockReasonPrompt
            onConfirm={confirmBlock}
            onCancel={() => setShowBlockReason(false)}
          />
        ) : null}

        {showCancelReason ? (
          <CancelReasonPrompt
            onConfirm={confirmCancel}
            onCancel={() => setShowCancelReason(false)}
          />
        ) : null}

        {showInfo && (booking?.id || clientHit.found) ? (
          <ClientInfoCard
            bookingId={booking?.id} clientId={clientHit.clientId} phone={phone}
            fallbackName={clientName} onClose={() => setShowInfo(false)}
          />
        ) : null}

        {gate.conflicts ? (
          <ConflictOverride
            conflicts={gate.conflicts}
            actionLabel={gate.actionLabel}
            onForce={() => { setSaving(true); void gate.retry(); }}
            onDismiss={gate.clear}
          />
        ) : null}
      </div>
    </div>
  );
}
